/**
 * Dashboard navigation modules with plan-aware disabled state.
 *
 * @module useDashboardModules
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { dashboardModules, type DashboardModule } from '@/data/dashboardData';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentPlan } from '@/hooks/useCurrentPlan';
import { requiresRenewal, SIMULATION_RENEWAL_NAV_HINT } from '@/lib/planAccess';

export function useDashboardModules(): readonly DashboardModule[] {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { plan } = useCurrentPlan();
  const isAdmin = user?.userType === 'admin';

  return useMemo(() => {
    if (isAdmin || !plan || !requiresRenewal(plan)) {
      return dashboardModules;
    }

    const disabledHint = t('dashboard.examNav.renewRequired', {
      defaultValue: SIMULATION_RENEWAL_NAV_HINT,
    });

    return dashboardModules.map((module) =>
      module.id === 'exam-simulation'
        ? { ...module, disabled: true, disabledHint }
        : module,
    );
  }, [isAdmin, plan, t]);
}
